import type { FC, ReactNode } from 'react';
import type {
  PopoverProps as AriaPopoverProps,
  Selection,
} from 'react-aria-components';

import { ChevronDown } from '@untitledui/icons/ChevronDown';
import { XClose } from '@untitledui/icons/XClose';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button as AriaButton,
  Header as AriaHeader,
  ListBox as AriaListBox,
  ListBoxSection as AriaListBoxSection,
  Separator as AriaSeparator,
} from 'react-aria-components';

import { HintText } from '@/components/base/input/hint-text';
import { Label } from '@/components/base/input/label';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { cx } from '@/utils/cx';

import { Divider } from '../divider/divider';
import { Popover } from './popover';
import { SelectContext, type SelectItemType, sizes } from './select';
import { SelectItem } from './select-item';

/**
 * Trigger padding/height for `triggerVariant="button"`, mirroring the standard
 * `Button` component's per-size spacing so a `size="xs"` filter button matches
 * other xs buttons instead of the old hardcoded `h-10`.
 */
const buttonTriggerSizes: Record<'xs' | 'sm' | 'md', string> = {
  xs: 'px-2.5 py-1.5',
  sm: 'px-3 py-2',
  md: 'h-10 px-3.5',
};

export type MultiSelectDropdownItem<T extends string = string> =
  | (Omit<SelectItemType, 'id'> & {
      id: T;
      /** Optional non-interactive heading rendered above this option. */
      groupLabel?: string;
    })
  | { type: 'divider' };

interface MultiSelectDropdownProps<T extends string = string> {
  /** Label text displayed above the select. */
  label?: string;
  /** Helper/hint text displayed below the select. */
  hint?: string;
  /** Tooltip on the label. */
  tooltip?: string;
  /** Size variant. */
  size?: 'xs' | 'sm' | 'md';
  /** Placeholder when no items selected. */
  placeholder?: string;
  /** The available options. Supports dividers between groups. */
  items: MultiSelectDropdownItem<T>[];
  /** Controlled selected keys. */
  selectedKeys: Iterable<T>;
  /** Called when selection changes. */
  onSelectionChange: (keys: Set<T>) => void;
  /** Max tags to show in the trigger before "+N". */
  maxDisplay?: number;
  /** Max number of items that can be selected. */
  maxSelections?: number;
  /** Additional className for the root container. */
  className?: string;
  /** Additional className for the popover dropdown. */
  popoverClassName?: string;
  /** Whether the select is disabled. */
  isDisabled?: boolean;
  /** Icon rendered at the trailing end of the trigger (replaces the chevron). */
  trailingIcon?: ReactNode;
  /** Whether the select is in an invalid/error state. */
  isInvalid?: boolean;
  /** Whether the field is required. */
  isRequired?: boolean;
  /** Accessible label for the listbox. */
  'aria-label'?: string;
  /** Header render function displayed above the list items. */
  header?: () => ReactNode;
  /** Footer render function for custom actions (e.g. "Clear all" / "Apply"). */
  footer?: (params: {
    selectedKeys: Set<T>;
    clearAll: () => void;
    close: () => void;
  }) => ReactNode;
  /** Icon component rendered inside the trigger when iconOnly is true. */
  triggerIcon?: FC<{ className?: string }>;
  /** Show only the triggerIcon without text/tags. */
  iconOnly?: boolean;
  /** When set, shows this single label instead of individual item pills. */
  triggerLabel?: string;
  /** Persistent label rendered before the selection pills (e.g. `Status [Qualified ×]`). */
  triggerPrefix?: string;
  /** Prevent tags from wrapping to multiple lines; the trigger grows horizontally instead. */
  noWrap?: boolean;
  /** Match the popover width to the trigger width. */
  matchTriggerWidth?: boolean;
  /** Render listbox rows with a leading checkbox instead of the trailing check on selected rows. */
  showCheckboxes?: boolean;
  /** Render listbox rows with a leading radio button (pair with `selectionMode="single"`). */
  showRadios?: boolean;
  /** Visual variant of the trigger — `input` (form-field style) or `button` (secondary-button style). */
  triggerVariant?: 'input' | 'button';
  /** Popover placement override; defaults to `bottom end` when `matchTriggerWidth` is false. */
  popoverPlacement?: AriaPopoverProps['placement'];
  /**
   * `single` keeps exactly one option selected (clicking the active option
   * doesn't clear it). The default is the multi-select behavior.
   */
  selectionMode?: 'multiple' | 'single';
  /**
   * Keys whose selection is not surfaced in the trigger (no pill, placeholder
   * stays). Lets a default option like "Show all" read as selected in the
   * list while the trigger presents it as "no filter active".
   */
  triggerHiddenKeys?: Iterable<T>;
}

export const MultiSelectDropdown = <T extends string = string>({
  items,
  selectedKeys: selectedKeysProp,
  onSelectionChange,
  placeholder = 'Select',
  maxDisplay,
  maxSelections,
  header,
  footer,
  triggerIcon: TriggerIcon,
  iconOnly = false,
  label,
  hint,
  tooltip,
  size = 'sm',
  isDisabled = false,
  isInvalid = false,
  isRequired = false,
  trailingIcon,
  className,
  popoverClassName,
  triggerLabel,
  triggerPrefix,
  noWrap = false,
  matchTriggerWidth = true,
  showCheckboxes = false,
  showRadios = false,
  triggerVariant = 'input',
  popoverPlacement,
  selectionMode = 'multiple',
  triggerHiddenKeys,
  'aria-label': ariaLabel,
}: MultiSelectDropdownProps<T>) => {
  const isButtonTrigger = triggerVariant === 'button';
  // Trigger label text size. Button-variant xs matches the standard xs Button
  // (text-xs); everything else keeps the prior sm/md behavior.
  const triggerTextClass =
    size === 'xs' ? 'text-xs' : size === 'sm' ? 'text-sm' : 'text-md';
  // Selection pills. At xs they drop to text-xs and zero vertical padding so a
  // selected pill doesn't make the trigger taller than sibling xs controls.
  const pillTextClass = size === 'xs' ? 'text-xs' : 'text-sm';
  const pillPadY = size === 'xs' ? 'py-0' : 'py-0.5';
  const [isOpen, setIsOpen] = useState(false);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listBoxRef = useRef<HTMLDivElement>(null);
  const [popoverWidth, setPopoverWidth] = useState('');

  // Move focus to the ListBox when the popover opens so keyboard
  // navigation (ArrowUp/Down, Enter/Space) works immediately.
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        listBoxRef.current?.focus();
      });
    }
  }, [isOpen]);

  const onResize = useCallback(() => {
    if (!matchTriggerWidth) return;
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPopoverWidth(`${rect.width}px`);
  }, [matchTriggerWidth]);

  useResizeObserver({ ref: triggerRef, onResize, box: 'border-box' });

  type OptionItem = Omit<SelectItemType, 'id'> & { id: T };

  const selectedKeySet = useMemo(() => {
    if (selectedKeysProp instanceof Set) return selectedKeysProp as Set<T>;
    return new Set(selectedKeysProp);
  }, [selectedKeysProp]);

  const optionItems = useMemo(
    () => items.filter((item): item is OptionItem => !('type' in item)),
    [items],
  );

  const hiddenTriggerKeySet = useMemo(
    () => new Set<T>(triggerHiddenKeys),
    [triggerHiddenKeys],
  );

  // Drives the trigger pills only — the listbox renders from `selectedKeySet`,
  // so a trigger-hidden key still shows as selected in the open list.
  const selectedItems = useMemo(
    () =>
      optionItems.filter(
        (item) =>
          selectedKeySet.has(item.id) && !hiddenTriggerKeySet.has(item.id),
      ),
    [optionItems, selectedKeySet, hiddenTriggerKeySet],
  );

  const displayedItems = maxDisplay
    ? selectedItems.slice(0, maxDisplay)
    : selectedItems;
  const hiddenCount =
    maxDisplay && selectedItems.length > maxDisplay
      ? selectedItems.length - maxDisplay
      : 0;

  const atMax =
    typeof maxSelections === 'number' && selectedKeySet.size >= maxSelections;

  const handleSelectionChange = (selection: Selection) => {
    if (selection === 'all') return;
    const next = selection as Set<T>;
    if (typeof maxSelections === 'number' && next.size > maxSelections) {
      return;
    }
    onSelectionChange(next);
    // A single-select is done after one pick; multi-select stays open so the
    // user can keep toggling options.
    if (selectionMode === 'single') close();
  };

  const removeItem = (id: T, e: React.SyntheticEvent) => {
    e.stopPropagation();
    const next = new Set(selectedKeySet);
    next.delete(id);
    onSelectionChange(next);
  };

  const clearAll = () => onSelectionChange(new Set<T>());

  const hasSelection = selectedItems.length > 0;

  return (
    <SelectContext.Provider value={{ size }}>
      <div className={cx('flex flex-col gap-1.5', className)}>
        {label && (
          <Label isRequired={isRequired} tooltip={tooltip}>
            {label}
          </Label>
        )}

        <AriaButton
          ref={triggerRef}
          isDisabled={isDisabled}
          aria-label={ariaLabel}
          onFocus={onResize}
          onHoverStart={onResize}
          onPress={toggle}
          className={cx(
            'relative flex w-full cursor-pointer items-center rounded-lg bg-primary ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset',
            isButtonTrigger
              ? 'shadow-xs-skeumorphic hover:bg-primary_hover'
              : 'shadow-xs',
            'focus-visible:ring-2 focus-visible:ring-brand',
            isOpen && 'ring-2 ring-brand',
            isDisabled && 'cursor-not-allowed bg-disabled_subtle text-disabled',
            isInvalid && 'ring-error_subtle',
            isInvalid && isOpen && 'ring-2 ring-error',
            iconOnly && 'w-auto',
            isButtonTrigger ? buttonTriggerSizes[size] : sizes[size].root,
          )}
        >
          {TriggerIcon && iconOnly ? (
            <TriggerIcon className="size-5 text-fg-quaternary" />
          ) : (
            <div className="flex w-full items-center gap-1.5">
              {triggerPrefix && hasSelection && (
                <span
                  className={cx(
                    'shrink-0 font-semibold text-primary',
                    triggerTextClass,
                  )}
                >
                  {triggerPrefix}
                </span>
              )}
              {hasSelection ? (
                <div
                  className={cx(
                    'flex min-w-0 flex-1 items-center gap-1',
                    noWrap ? 'flex-nowrap' : 'flex-wrap',
                  )}
                >
                  {triggerLabel ? (
                    <span
                      className={cx(
                        'flex shrink-0 items-center rounded-md bg-brand-primary px-1.5 text-brand-primary',
                        pillTextClass,
                        pillPadY,
                      )}
                    >
                      {triggerLabel}
                    </span>
                  ) : (
                    <>
                      {displayedItems.map((item) => (
                        <span
                          key={item.id}
                          className={cx(
                            'flex items-center gap-0.5 rounded-md bg-brand-primary pr-0.5 pl-1.5 text-brand-primary',
                            // A single-selection pill is the field's whole
                            // value, so it truncates within the trigger rather
                            // than overflowing it (a long project name spilled
                            // past the field edge). Multi pills keep their
                            // natural width for the wrap / +N presentation.
                            selectionMode === 'single' ? 'min-w-0' : 'shrink-0',
                            pillTextClass,
                            pillPadY,
                          )}
                        >
                          <span className="truncate">{item.label}</span>
                          {!isDisabled && (
                            <span
                              role="button"
                              tabIndex={0}
                              className="flex cursor-pointer items-center rounded-sm p-0.5 text-fg-quaternary transition hover:bg-primary_hover hover:text-fg-quaternary_hover"
                              onClick={(e) => removeItem(item.id, e)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  removeItem(item.id, e);
                                }
                              }}
                              aria-label={`Remove ${item.label}`}
                            >
                              <XClose className="size-3 stroke-[3px]" />
                            </span>
                          )}
                        </span>
                      ))}

                      {hiddenCount > 0 && (
                        <span
                          className={cx(
                            'shrink-0 rounded-md bg-brand-primary px-1.5 text-brand-primary',
                            pillTextClass,
                            pillPadY,
                          )}
                        >
                          +{hiddenCount}
                        </span>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <span
                  className={cx(
                    'flex-1 truncate text-left',
                    triggerTextClass,
                    isButtonTrigger
                      ? 'font-semibold text-primary'
                      : triggerPrefix
                        ? 'font-medium text-secondary'
                        : 'text-placeholder',
                  )}
                >
                  {placeholder}
                </span>
              )}

              {selectedItems.length > 1 && !isDisabled && (
                <span
                  role="button"
                  tabIndex={0}
                  className="flex shrink-0 cursor-pointer items-center rounded-sm p-0.5 text-fg-quaternary transition hover:bg-primary_hover hover:text-fg-quaternary_hover"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      clearAll();
                    }
                  }}
                  aria-label="Clear all selections"
                >
                  <XClose className="size-4 stroke-[2.5px]" />
                </span>
              )}

              {trailingIcon ?? (
                <ChevronDown
                  aria-hidden="true"
                  className={cx(
                    'ml-auto shrink-0 text-fg-quaternary transition-transform',
                    size === 'md' ? 'size-5' : 'size-4 stroke-[2.5px]',
                    isOpen && 'rotate-180',
                  )}
                />
              )}
            </div>
          )}
        </AriaButton>

        <Popover
          size={size}
          triggerRef={triggerRef}
          isOpen={isOpen}
          onOpenChange={close}
          style={matchTriggerWidth ? { width: popoverWidth } : undefined}
          placement={
            popoverPlacement ?? (!matchTriggerWidth ? 'bottom end' : undefined)
          }
          className={cx(
            // The popover's base styles carry `min-w-fit`, which lets one long
            // unbreakable option override the measured width and blow the
            // popover out past the trigger (and the modal it sits in). When
            // the whole point is matching the trigger, drop that floor — the
            // options themselves truncate.
            matchTriggerWidth && 'min-w-0',
            popoverClassName,
          )}
        >
          {header && (
            <>
              {header()}
              <Divider className="my-1 h-px w-full bg-border-secondary" />
            </>
          )}

          <AriaListBox
            ref={listBoxRef}
            selectionMode={selectionMode}
            disallowEmptySelection={selectionMode === 'single'}
            selectedKeys={selectedKeySet}
            onSelectionChange={handleSelectionChange}
            className="size-full outline-hidden"
            aria-label={ariaLabel ?? label ?? 'Select options'}
            escapeKeyBehavior="none" // Press Escape to close the dropdown instead of clearing all selections
          >
            {items.map((item, index) => {
              if ('type' in item) {
                return (
                  <AriaSeparator
                    // oxlint-disable-next-line react/no-array-index-key -- dividers have no unique identifier
                    key={`divider-${index}`}
                    className="my-1 h-px w-full bg-border-secondary"
                  />
                );
              }
              const { groupLabel, ...selectItem } = item;
              const option = (
                <SelectItem
                  key={item.id}
                  {...selectItem}
                  showCheckbox={showCheckboxes}
                  showRadio={showRadios}
                  isDisabled={
                    item.isDisabled || (atMax && !selectedKeySet.has(item.id))
                  }
                />
              );
              if (!groupLabel) return option;
              return (
                <AriaListBoxSection key={item.id}>
                  <AriaHeader className="px-3 pt-2 pb-1 text-xs font-semibold text-tertiary">
                    {groupLabel}
                  </AriaHeader>
                  {option}
                </AriaListBoxSection>
              );
            })}
          </AriaListBox>

          {footer && (
            <>
              <Divider className="my-1 h-px w-full bg-border-secondary" />
              {footer({
                selectedKeys: selectedKeySet,
                clearAll,
                close,
              })}
            </>
          )}
        </Popover>

        {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
      </div>
    </SelectContext.Provider>
  );
};
